import React from "react";
import call from "./call";
import useQuery, { useGetQueryData, useMutateBody } from "./useQuery";
import { useChannelUpdates } from "./ws/useChannelUpdates";
import { comparatorBy } from "diagnostics-lib/comparator";
const comparator = comparatorBy("createdAt");

export const useManualFailures = (projectId: string) => {
  const getCachedManualFailures = useGetQueryData("/projects/:projectId(uuid)/manual-failures", {
    routeParams: { projectId },
  });

  const readItems = React.useCallback(
    () => getCachedManualFailures()?.body,
    [getCachedManualFailures],
  );

  const fetchUpdatedItems = React.useCallback(
    async (since: string) => {
      const { manualFailures, deletedManualFailureIds } = (
        await call("GET", "/projects/:projectId(uuid)/manual-failures/updates/:since(date)", {
          routeParams: { projectId, since: new Date(since) },
        })
      ).body;
      return [manualFailures, deletedManualFailureIds] as const;
    },
    [projectId],
  );

  useChannelUpdates(
    { key: "manual_failures", projectId },
    {
      fetchUpdatedItems,
      mutateItems: useMutateBody("/projects/:projectId(uuid)/manual-failures", {
        routeParams: { projectId },
      }),
      readItems,
    },
  );

  const manualFailures = useQuery("/projects/:projectId(uuid)/manual-failures", {
    routeParams: { projectId },
  }).body;

  return React.useMemo(() => manualFailures.sort(comparator), [manualFailures]);
};

export const useManualFailureIndex = (projectId: string) => {
  const manualFailures = useManualFailures(projectId);

  return React.useMemo(
    () =>
      manualFailures.reduce(
        (acc, manualFailure) => {
          acc[manualFailure.id] = manualFailure;
          return acc;
        },
        {} as Record<string, ManualFailure>,
      ),
    [manualFailures],
  );
};

export type ManualFailure = ReturnType<typeof useManualFailures>[number];
export type IndexedManualFailure = ReturnType<typeof useManualFailureIndex>;
